import { z } from 'zod'

/**
 * Client config from import.meta.env.
 *
 * Validated at startup for the same reason as on the backend: better to
 * fail immediately with an explanation than to end up with `undefined`
 * inside an assembled URL and chase a 404 with a cryptic message.
 *
 * Reminder: everything here is PUBLIC. VITE_ variables are inlined into
 * the bundle, so a secret put here is a secret published in the source.
 */
const ClientEnvSchema = z.object({
  VITE_API_BASE_URL: z.string().url(),
  VITE_DEV_EXTERNAL_ID: z.string().min(1).default('local-player-1'),
  /**
   * Operator origins allowed to talk to the game over postMessage, comma
   * separated ("https://lobby.acme,https://acme.com").
   *
   * The same list belongs in the CSP `frame-ancestors` of whatever serves
   * this bundle — that one decides who may frame us, this one decides who
   * we exchange messages with. Empty is the correct default: a standalone
   * demo has no operator, and the bridge simply stays quiet.
   */
  VITE_OPERATOR_ORIGINS: z.string().default(''),
})

const parsed = ClientEnvSchema.safeParse(import.meta.env)

if (!parsed.success) {
  const details = parsed.error.issues
    .map((i) => `  • ${i.path.join('.')}: ${i.message}`)
    .join('\n')
  throw new Error(
    `Invalid client configuration:\n${details}\n\n` +
      `Copy packages/client/.env.example to .env`,
  )
}

/**
 * "https://a.example, https://b.example/" → ['https://a.example', 'https://b.example'].
 *
 * Each entry is normalised through URL so that a trailing slash or a
 * default port does not silently fail to match `event.origin`, which is
 * always the bare scheme://host[:port]. Anything unparseable — and "*" in
 * particular — is dropped loudly: a wildcard here would mean posting the
 * player's balance to whoever framed us.
 */
function parseOrigins(raw: string): string[] {
  const out: string[] = []
  for (const item of raw.split(',').map((s) => s.trim()).filter(Boolean)) {
    try {
      out.push(new URL(item).origin)
    } catch {
      throw new Error(
        `VITE_OPERATOR_ORIGINS: "${item}" is not an origin. ` +
          `Expected scheme://host[:port]; wildcards are not accepted.`,
      )
    }
  }
  return out
}

export const config = {
  apiBaseUrl: parsed.data.VITE_API_BASE_URL.replace(/\/+$/, ''),
  devExternalId: parsed.data.VITE_DEV_EXTERNAL_ID,
  operatorOrigins: parseOrigins(parsed.data.VITE_OPERATOR_ORIGINS),
} as const

// ── Board geometry ───────────────────────────────────────────────────────
// The grid size is set by the GAME (game.layout), not by a constant:
// otherwise a new 7×7 game would be drawn on top of a 6×5 board. Only what
// does not depend on the game stays here: the base tile size and the
// largest box the board has to fit into.

/** The largest board that still fits the desktop layout. */
const MAX_BOARD = { width: 620, height: 472 } as const

/** Base tile of a ways board. */
export const WAYS_TILE = { symbolSize: 116, gap: 10 } as const
/** Base tile of a tumble board: twice as many cells, smaller symbol. */
export const TUMBLE_TILE = { symbolSize: 88, gap: 8 } as const

export interface BoardLayout {
  reels: number
  rows: number
  symbolSize: number
  gap: number
  /** Step between the centres of neighbouring cells. */
  pitch: number
  boardWidth: number
  boardHeight: number
  /** ways: how many tiles a reel holds — visible rows plus one buffer each. */
  tilesPerReel: number
}

/**
 * Board geometry for one specific grid.
 *
 * The tile keeps its base size, but if the board does not fit into
 * MAX_BOARD it is scaled down proportionally. For the 5×3 and 6×5 grids
 * the factor is 1, so the existing games look exactly as they did before
 * the lobby appeared. A theme with an explicit `board.cellSize` passes
 * `max = null`: that size is a design decision, and the page CSS — not the
 * layout — is what shrinks the canvas on a narrow or short viewport.
 */
export function boardLayout(
  reels: number,
  rows: number,
  base: { symbolSize: number; gap: number },
  /** Board cap; `null` = no cap (the theme chose the cell size on purpose). */
  max: { width: number; height: number } | null = MAX_BOARD,
): BoardLayout {
  const fullWidth = reels * base.symbolSize + (reels - 1) * base.gap
  const fullHeight = rows * base.symbolSize + (rows - 1) * base.gap
  const fit = max ? Math.min(1, max.width / fullWidth, max.height / fullHeight) : 1

  const symbolSize = Math.floor(base.symbolSize * fit)
  const gap = Math.max(4, Math.round(base.gap * fit))

  return {
    reels,
    rows,
    symbolSize,
    gap,
    pitch: symbolSize + gap,
    boardWidth: reels * symbolSize + (reels - 1) * gap,
    boardHeight: rows * symbolSize + (rows - 1) * gap,
    tilesPerReel: rows + 2,
  }
}

/**
 * Tile base + cap for a game, given the theme's board overrides.
 * `cellSize` set ⇒ that size, uncapped; otherwise the mechanic's default
 * under MAX_BOARD. `gap` overrides independently.
 */
export function tileFor(
  base: { symbolSize: number; gap: number },
  board: { cellSize: number | null; gap: number | null } | undefined,
): { tile: { symbolSize: number; gap: number }; max: typeof MAX_BOARD | null } {
  return {
    tile: { symbolSize: board?.cellSize ?? base.symbolSize, gap: board?.gap ?? base.gap },
    max: board?.cellSize != null ? null : MAX_BOARD,
  }
}

// ── Animation timings ────────────────────────────────────────────────────
//
// They used to live here as constants. They are theme data now: defaults in
// `theme/resolve.ts`, allowed ranges in `theme/schema.ts`. How long a
// cascade takes is a design decision and must not require a code change —
// but only inside a band, because cascade length also sets spins per hour,
// which is an operator-facing number.
